// Moteur de validation dynamique des formulaires de démarche : à partir du schéma, il calcule la
// visibilité des sections et des champs (conditions évaluées dans l'ordre des dépendances, un champ
// masqué étant considéré comme non renseigné), construit un validateur Zod par champ visible,
// applique les valeurs par défaut et les calculs. Fonctions pures, sans dépendance au serveur :
// l'API et les interfaces utilisent exactement le même code.
import { z } from 'zod';
import {
  dependancesVisibilite,
  ordreTopologique,
  schemaFormulaire,
  tousLesChamps,
  type Calcul,
  type Champ,
  type Colonne,
  type Condition,
  type Formulaire,
  type ValidationChamp,
} from './demarches-formulaire';

export type DonneesFormulaire = Record<string, unknown>;

export interface ErreurChamp {
  champ: string;
  message: string;
}

export interface OptionsValidation {
  /** Brouillon : les champs obligatoires non renseignés ne sont pas signalés. */
  partiel?: boolean;
  /** Montants des tarifs connus, par code de rubrique (calculs référant à un tarif). */
  tarifs?: Record<string, number>;
  /** Identifiants des pièces utilisables par les champs fichier ; non contrôlé si absent. */
  pieces?: ReadonlySet<string>;
}

export interface ResultatValidation {
  valide: boolean;
  /** Données normalisées : champs visibles renseignés et champs calculés seulement. */
  donnees: DonneesFormulaire;
  erreurs: ErreurChamp[];
  /** Codes des champs visibles. */
  champsVisibles: string[];
  sectionsVisibles: string[];
}

/** Une valeur est « non renseignée » si elle est absente, nulle, vide ou un tableau vide. */
export function estVide(v: unknown): boolean {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);
}

function comparer(a: unknown, b: unknown): number | null {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'string' && typeof b === 'string') return a < b ? -1 : a > b ? 1 : 0;
  return null;
}

/** Évalue une condition sur des valeurs (un champ absent ou masqué vaut « non renseigné »). */
export function evaluerCondition(c: Condition | undefined, valeurs: DonneesFormulaire): boolean {
  if (!c) return true;
  if ('et' in c) return c.et.every((x) => evaluerCondition(x, valeurs));
  if ('ou' in c) return c.ou.some((x) => evaluerCondition(x, valeurs));
  const v = valeurs[c.champ];
  switch (c.operateur) {
    case 'renseigne':
      return !estVide(v);
    case 'non_renseigne':
      return estVide(v);
    case 'egal':
      return Array.isArray(v) ? false : v === c.valeur;
    case 'different':
      return Array.isArray(v) ? true : v !== c.valeur;
    case 'contient':
      if (Array.isArray(v)) return v.includes(c.valeur);
      return typeof v === 'string' && typeof c.valeur === 'string' && v.toLowerCase().includes(c.valeur.toLowerCase());
    default: {
      const d = comparer(v, c.valeur);
      if (d === null) return false;
      if (c.operateur === 'superieur') return d > 0;
      if (c.operateur === 'superieur_ou_egal') return d >= 0;
      if (c.operateur === 'inferieur') return d < 0;
      return d <= 0;
    }
  }
}

/** Visibilité de chaque section et de chaque champ pour des données brutes. */
export function visibilites(f: Formulaire, brutes: DonneesFormulaire): { sections: Set<string>; champs: Set<string> } {
  const liste = tousLesChamps(f);
  const deps = new Map(liste.map(({ section, champ }) => [champ.code, dependancesVisibilite(section, champ)]));
  const ordre = ordreTopologique(deps) ?? liste.map((x) => x.champ.code);
  const parCode = new Map(liste.map((x) => [x.champ.code, x]));
  const valeurs: DonneesFormulaire = {};
  const champs = new Set<string>();
  for (const code of ordre) {
    const x = parCode.get(code);
    if (!x) continue;
    if (evaluerCondition(x.section.visible_si, valeurs) && evaluerCondition(x.champ.visible_si, valeurs)) {
      champs.add(code);
      valeurs[code] = brutes[code];
    }
  }
  const sections = new Set(f.sections.filter((s) => s.champs.some((c) => champs.has(c.code))).map((s) => s.code));
  return { sections, champs };
}

const dateValide = (d: string) => {
  const t = Date.parse(`${d}T00:00:00Z`);
  return !Number.isNaN(t) && new Date(t).toISOString().slice(0, 10) === d;
};

function texte(v: ValidationChamp | undefined, maxDefaut: number): z.ZodType {
  let s = z.string().trim().max(v?.longueur_max ?? maxDefaut, `${v?.longueur_max ?? maxDefaut} caractères au plus`);
  if (v?.longueur_min) s = s.min(v.longueur_min, `${v.longueur_min} caractères au moins`);
  if (v?.motif) s = s.regex(new RegExp(`^(?:${v.motif})$`), v.message_motif ?? 'Format invalide');
  return s;
}

function nombre(v: ValidationChamp | undefined, entier: boolean): z.ZodType {
  let n = entier ? z.number().int('Nombre entier attendu').min(0, 'Montant positif attendu') : z.number().finite();
  if (v?.min !== undefined) n = n.min(v.min, `Valeur minimale : ${v.min}`);
  if (v?.max !== undefined) n = n.max(v.max, `Valeur maximale : ${v.max}`);
  return n;
}

function choix(options: { valeur: string }[] | undefined): z.ZodType<string> {
  const valeurs = (options ?? []).map((o) => o.valeur);
  return z.string().refine((x) => valeurs.includes(x), 'Choix non proposé');
}

/** Validateur de la valeur d'un champ ou d'une colonne (le caractère obligatoire est traité à part). */
export function validateurValeur(c: Pick<Champ, 'type' | 'validation' | 'options' | 'colonnes'>, options: OptionsValidation = {}): z.ZodType {
  const v = c.validation;
  switch (c.type) {
    case 'texte_court':
      return texte(v, 255);
    case 'texte_long':
      return texte(v, 5000);
    case 'nombre':
      return nombre(v, false);
    case 'montant':
      return nombre(v, true);
    case 'date':
      return z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date au format AAAA-MM-JJ')
        .refine(dateValide, 'Date inexistante')
        .refine((d) => !v?.date_min || d >= v.date_min, `Date au plus tôt : ${v?.date_min}`)
        .refine((d) => !v?.date_max || d <= v.date_max, `Date au plus tard : ${v?.date_max}`);
    case 'choix_unique':
      return choix(c.options);
    case 'choix_multiple': {
      let a = z.array(choix(c.options)).refine((x) => new Set(x).size === x.length, 'Choix en double');
      if (v?.min !== undefined) a = a.refine((x) => x.length >= (v.min as number), `${v.min} choix au moins`);
      if (v?.max !== undefined) a = a.refine((x) => x.length <= (v.max as number), `${v.max} choix au plus`);
      return a;
    }
    case 'oui_non':
      return z.boolean({ error: 'Oui ou non attendu' });
    case 'telephone':
      return z.string().trim().regex(/^\+?[0-9][0-9 .-]{5,19}$/, 'Numéro de téléphone invalide');
    case 'courriel':
      return z.string().trim().email('Courriel invalide').max(254);
    case 'adresse':
      return z.strictObject({
        voie: z.string().trim().min(1, 'Voie obligatoire').max(300),
        quartier: z.string().trim().max(120).optional(),
        ville: z.string().trim().max(120).optional(),
        complement: z.string().trim().max(300).optional(),
      });
    case 'localisation':
      return z.strictObject({ latitude: z.number().min(-90).max(90), longitude: z.number().min(-180).max(180) });
    case 'fichier':
      return z
        .string()
        .uuid('Identifiant de pièce attendu')
        .refine((id) => !options.pieces || options.pieces.has(id), 'Pièce inconnue pour ce dossier');
    case 'tableau': {
      let a = z.array(ligneTableau(c.colonnes ?? [])).max(500, '500 lignes au plus');
      if (v?.min !== undefined) a = a.refine((x) => x.length >= (v.min as number), `${v.min} lignes au moins`);
      if (v?.max !== undefined) a = a.refine((x) => x.length <= (v.max as number), `${v.max} lignes au plus`);
      return a;
    }
  }
}

function ligneTableau(colonnes: Colonne[]): z.ZodType {
  const forme: Record<string, z.ZodType> = {};
  for (const k of colonnes) {
    const base = validateurValeur({ type: k.type, validation: k.validation, options: k.options });
    forme[k.code] = k.obligatoire ? base : base.nullable().optional();
  }
  return z.strictObject(forme);
}

function arrondir(type: Champ['type'], x: number): number {
  // Montants : entiers en francs CFA, arrondis au franc supérieur (H4-04).
  return type === 'montant' ? Math.ceil(x - 1e-9) : Math.round(x * 1e6) / 1e6;
}

function valeurOperande(o: Calcul['operandes'][number], donnees: DonneesFormulaire, tarifs: Record<string, number>): number | null {
  if ('constante' in o) return o.constante;
  if ('tarif' in o) return typeof tarifs[o.tarif] === 'number' ? (tarifs[o.tarif] as number) : null;
  if ('champ' in o) return typeof donnees[o.champ] === 'number' ? (donnees[o.champ] as number) : null;
  const lignes = donnees[o.colonne.tableau];
  if (!Array.isArray(lignes)) return 0;
  return lignes.reduce<number>((s, l) => s + (typeof (l as Record<string, unknown>)?.[o.colonne.colonne] === 'number' ? ((l as Record<string, number>)[o.colonne.colonne] as number) : 0), 0);
}

/** Résultat d'un calcul : somme (opérande de champ absent compté zéro) ou produit (nul si un opérande manque). */
export function evaluerCalcul(calcul: Calcul, donnees: DonneesFormulaire, tarifs: Record<string, number> = {}): number | null {
  const valeurs = calcul.operandes.map((o) => ({ o, v: valeurOperande(o, donnees, tarifs) }));
  if (valeurs.some((x) => x.v === null && !('champ' in x.o && calcul.operation === 'somme'))) return null;
  const nombres = valeurs.map((x) => x.v ?? 0);
  return calcul.operation === 'somme' ? nombres.reduce((a, b) => a + b, 0) : nombres.reduce((a, b) => a * b, 1);
}

/** Valeurs par défaut déclarées par le schéma. */
export function valeursParDefaut(f: Formulaire): DonneesFormulaire {
  const r: DonneesFormulaire = {};
  for (const { champ } of tousLesChamps(f)) if (champ.valeur_defaut !== undefined && !champ.calcul) r[champ.code] = champ.valeur_defaut;
  return r;
}

const cheminErreur = (code: string, chemin: PropertyKey[]) => [code, ...chemin.map(String)].join('.');

/**
 * Valide des données saisies : champs inconnus refusés, valeurs par défaut appliquées aux champs
 * absents, champs masqués ignorés, obligatoires contrôlés (sauf brouillon), valeurs typées, calculs
 * recalculés par le serveur.
 */
export function validerDonnees(f: Formulaire, brutes: DonneesFormulaire, options: OptionsValidation = {}): ResultatValidation {
  const liste = tousLesChamps(f);
  const parCode = new Map(liste.map((x) => [x.champ.code, x.champ]));
  const erreurs: ErreurChamp[] = [];
  for (const cle of Object.keys(brutes)) if (!parCode.has(cle)) erreurs.push({ champ: cle, message: 'Champ inconnu dans ce formulaire' });
  const entree: DonneesFormulaire = { ...valeursParDefaut(f) };
  for (const [cle, valeur] of Object.entries(brutes)) if (parCode.has(cle)) entree[cle] = valeur;
  const vis = visibilites(f, entree);
  const donnees: DonneesFormulaire = {};
  for (const { champ } of liste) {
    if (!vis.champs.has(champ.code) || champ.calcul) continue;
    const valeur = entree[champ.code];
    if (estVide(valeur)) {
      if (champ.obligatoire && !options.partiel) erreurs.push({ champ: champ.code, message: `${champ.libelle} : champ obligatoire` });
      continue;
    }
    const r = validateurValeur(champ, options).safeParse(valeur);
    if (r.success) donnees[champ.code] = r.data;
    else for (const i of r.error.issues) erreurs.push({ champ: cheminErreur(champ.code, i.path), message: i.message });
  }
  const calcules = liste.filter((x) => x.champ.calcul && vis.champs.has(x.champ.code)).map((x) => x.champ);
  const deps = new Map(calcules.map((c) => [c.code, (c.calcul?.operandes ?? []).flatMap((o) => ('champ' in o ? [o.champ] : []))]));
  const ordre = ordreTopologique(deps) ?? calcules.map((c) => c.code);
  for (const code of ordre) {
    const c = parCode.get(code);
    if (!c?.calcul) continue;
    const v = evaluerCalcul(c.calcul, donnees, options.tarifs);
    if (v !== null) donnees[code] = arrondir(c.type, v);
  }
  return { valide: erreurs.length === 0, donnees, erreurs, champsVisibles: [...vis.champs], sectionsVisibles: [...vis.sections] };
}

/** Schéma de formulaire complet : structure, plus valeurs par défaut conformes à leur type. */
export const schemaFormulaireValide = schemaFormulaire.superRefine((f, ctx) => {
  f.sections.forEach((s, i) =>
    s.champs.forEach((c, j) => {
      if (c.valeur_defaut === undefined) return;
      if (c.calcul) {
        ctx.addIssue({ code: 'custom', message: 'Un champ calculé n\'a pas de valeur par défaut', path: ['sections', i, 'champs', j, 'valeur_defaut'] });
        return;
      }
      if (c.type === 'fichier' || !validateurValeur(c).safeParse(c.valeur_defaut).success) {
        ctx.addIssue({ code: 'custom', message: 'Valeur par défaut incompatible avec le type du champ', path: ['sections', i, 'champs', j, 'valeur_defaut'] });
      }
    }),
  );
});

/** Champs fichier renseignés et pièces qu'ils désignent (contrôle du dépôt). */
export function piecesReferencees(f: Formulaire, donnees: DonneesFormulaire): { champ: string; type_piece: string | null; piece: string }[] {
  return tousLesChamps(f)
    .filter((x) => x.champ.type === 'fichier' && typeof donnees[x.champ.code] === 'string')
    .map((x) => ({ champ: x.champ.code, type_piece: x.champ.type_piece ?? null, piece: donnees[x.champ.code] as string }));
}
